import { priceBankedWalk, storeMovementPct } from '../movement/banked-walk.js';

export const OBJECT_MANIPULATION_COMMAND_SCHEMA = 'ft.ft3d.object-manipulation.command.v1' as const;
export const OBJECT_MANIPULATION_RECEIPT_SCHEMA = 'ft.ft3d.object-manipulation.receipt.v1' as const;
export const OBJECT_MANIPULATION_COMBAT_INITIATION_AP = 1;
export const OBJECT_MANIPULATION_SIZE_RANKS = 8;

export type ObjectManipulationVerb = 'push' | 'pull';
export type ObjectManipulationReason =
  | 'invalid-verb'
  | 'no-active-actor'
  | 'not-controlled'
  | 'wrong-turn'
  | 'immobile'
  | 'stale-actor'
  | 'stale-object'
  | 'fixed-object'
  | 'occupied-object'
  | 'stacked-object'
  | 'unknown-size'
  | 'object-too-large'
  | 'object-too-small'
  | 'unknown-weight'
  | 'over-capacity'
  | 'hands-unverified'
  | 'hands-full'
  | 'reach-unverified'
  | 'out-of-reach'
  | 'no-movement'
  | 'unsupported-direction'
  | 'unsupported-route'
  | 'blocked-path'
  | 'support-changed'
  | 'insufficient-ap'
  | 'insufficient-movement'
  | 'interrupted';

export interface ObjectManipulationPoint {
  x: number;
  y: number;
  z: number;
}

export interface ObjectManipulationGateInput {
  verb: ObjectManipulationVerb | string;
  /** TELEKINETIC push/pull (2026-10-07, DOS2-style — object_interaction_range_ft): the actor stays where it is and moves an unattended
   *  object by mind. No hands, any direction; `actor.reachFt` is the telekinetic range; the size window runs from one size smaller than
   *  the actor up to `telekineticMaxSizeRank` (throw_object_max_size_type's rank, when larger). */
  telekinetic?: boolean;
  telekineticMaxSizeRank?: number | null;
  actor: {
    present: boolean;
    controlled: boolean;
    canAct: boolean;
    sizeRank: number | null;
    robustBuild: boolean;
    freeHands: number | null;
    pushDragLiftCapacityLb: number | null;
    reachFt: number | null;
    distanceToObjectFt: number | null;
    position: ObjectManipulationPoint | null;
    expectedPosition: ObjectManipulationPoint | null;
    revision: number | null;
    expectedRevision: number | null;
    aps: number | null;
    movementPct: number | null;
    walkingSpeedFt: number | null;
  };
  object: {
    sizeRank: number | null;
    grossWeightLb: number | null;
    quantity: number | null;
    looseEncounterObject: boolean;
    fixed: boolean;
    occupied: boolean;
    position: ObjectManipulationPoint | null;
    expectedPosition: ObjectManipulationPoint | null;
    fingerprint: string | null;
    expectedFingerprint: string | null;
  };
  encounter: {
    inCombat: boolean;
    actorOwnsTurn: boolean;
  };
  route: {
    distanceFt: number | null;
    directionValid: boolean;
    sameSupportPlane: boolean;
    supported: boolean;
    pathClear: boolean;
    supportUnchanged: boolean;
  };
}

export interface ObjectManipulationEconomyDecision {
  initiationApCost: number;
  movementApsSpent: number;
  movementPctAfter: number;
  apsAfter: number;
}

export type ObjectManipulationGateResult =
  | { allowed: false; reason: ObjectManipulationReason; effectiveActorSizeRank: number | null; economy: null }
  | { allowed: true; reason: null; effectiveActorSizeRank: number; economy: ObjectManipulationEconomyDecision | null };

const POSITION_EPS_FT = 0.01;
const WEIGHT_EPS_LB = 0.0001;

export interface DragStrengthInput {
  draggers: ReadonlyArray<{ id: string; capacityLb: number | null }>;
  object: { fixed: boolean; grossWeightLb: number | null };
}
export type DragStrengthVerdict = { ok: true; capacityLb: number; weightLb: number }
  | { ok: false; reason: 'fixed-object' | 'over-capacity' | 'unknown-weight'; capacityLb: number | null; weightLb: number | null };

/** The Push/Pull strength rule, shared by an operator and a latched draft team. */
export function dragStrengthVerdict(input: DragStrengthInput): DragStrengthVerdict {
  const capacities = input.draggers.map(d => d.capacityLb);
  const capacityLb = capacities.every(c => finite(c) && c >= 0)
    ? capacities.reduce<number>((sum, c) => sum + c!, 0) : null;
  const weightLb = input.object.grossWeightLb;
  const facts = { capacityLb, weightLb };
  if (input.object.fixed) return { ...facts, ok: false, reason: 'fixed-object' };
  if (!finite(weightLb) || weightLb < 0 || capacityLb === null || !finite(capacityLb))
    return { ...facts, ok: false, reason: 'unknown-weight' };
  if (weightLb > capacityLb + WEIGHT_EPS_LB) return { ...facts, ok: false, reason: 'over-capacity' };
  return { ok: true, capacityLb, weightLb };
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function rank(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0 && Number(value) < OBJECT_MANIPULATION_SIZE_RANKS;
}

function point(value: ObjectManipulationPoint | null): value is ObjectManipulationPoint {
  return value !== null && finite(value.x) && finite(value.y) && finite(value.z);
}

function samePoint(a: ObjectManipulationPoint | null, b: ObjectManipulationPoint | null): boolean {
  return point(a) && point(b)
    && Math.abs(a.x - b.x) <= POSITION_EPS_FT
    && Math.abs(a.y - b.y) <= POSITION_EPS_FT
    && Math.abs(a.z - b.z) <= POSITION_EPS_FT;
}

export function objectManipulationEffectiveSizeRank(
  sizeRank: number | null,
  robustBuild: boolean,
): number | null {
  if (!rank(sizeRank)) return null;
  return Math.min(sizeRank + (robustBuild ? 1 : 0), OBJECT_MANIPULATION_SIZE_RANKS - 1);
}

function refuse(reason: ObjectManipulationReason, effectiveActorSizeRank: number | null): ObjectManipulationGateResult {
  return { allowed: false, reason, effectiveActorSizeRank, economy: null };
}

/**
 * Pure policy gate over canonical inventory, creature, reach, position and movement facts.
 * The browser preflights trap and movement effects before this result can be saved.
 */
export function objectManipulationGate(input: ObjectManipulationGateInput): ObjectManipulationGateResult {
  const effectiveActorSizeRank = objectManipulationEffectiveSizeRank(input.actor.sizeRank, input.actor.robustBuild);
  if (input.verb !== 'push' && input.verb !== 'pull') return refuse('invalid-verb', effectiveActorSizeRank);
  if (!input.actor.present) return refuse('no-active-actor', effectiveActorSizeRank);
  if (!input.actor.controlled) return refuse('not-controlled', effectiveActorSizeRank);
  if (input.encounter.inCombat && !input.encounter.actorOwnsTurn) return refuse('wrong-turn', effectiveActorSizeRank);
  if (!input.actor.canAct) return refuse('immobile', effectiveActorSizeRank);

  if (!Number.isInteger(input.actor.revision) || Number(input.actor.revision) < 0
    || input.actor.revision !== input.actor.expectedRevision
    || !samePoint(input.actor.position, input.actor.expectedPosition)) {
    return refuse('stale-actor', effectiveActorSizeRank);
  }
  if (!samePoint(input.object.position, input.object.expectedPosition)
    || typeof input.object.fingerprint !== 'string' || input.object.fingerprint === ''
    || input.object.fingerprint !== input.object.expectedFingerprint) {
    return refuse('stale-object', effectiveActorSizeRank);
  }

  const strength = dragStrengthVerdict({ draggers: [{ id: 'actor', capacityLb: input.actor.pushDragLiftCapacityLb }],
    object: { fixed: !input.object.looseEncounterObject || input.object.fixed, grossWeightLb: input.object.grossWeightLb } });
  if (!strength.ok && strength.reason === 'fixed-object') return refuse(strength.reason, effectiveActorSizeRank);
  if (input.object.occupied) return refuse('occupied-object', effectiveActorSizeRank);
  if (input.object.quantity !== 1) return refuse('stacked-object', effectiveActorSizeRank);
  if (effectiveActorSizeRank === null || !rank(input.object.sizeRank)) return refuse('unknown-size', effectiveActorSizeRank);
  const telekinetic = input.telekinetic === true;
  const maxRank = telekinetic && rank(input.telekineticMaxSizeRank) ? Math.max(effectiveActorSizeRank - 1, input.telekineticMaxSizeRank) : effectiveActorSizeRank - 1;
  if (input.object.sizeRank > maxRank) return refuse('object-too-large', effectiveActorSizeRank);
  if (input.object.sizeRank < effectiveActorSizeRank - 1) return refuse('object-too-small', effectiveActorSizeRank);

  if (!strength.ok) return refuse(strength.reason, effectiveActorSizeRank);
  if (!telekinetic) { // the mind needs no free hand
    if (!Number.isInteger(input.actor.freeHands) || Number(input.actor.freeHands) < 0) {
      return refuse('hands-unverified', effectiveActorSizeRank);
    }
    if (Number(input.actor.freeHands) < 1) return refuse('hands-full', effectiveActorSizeRank);
  }
  if (!finite(input.actor.reachFt) || input.actor.reachFt < 0
    || !finite(input.actor.distanceToObjectFt) || input.actor.distanceToObjectFt < 0) {
    return refuse('reach-unverified', effectiveActorSizeRank);
  }
  if (input.actor.distanceToObjectFt > input.actor.reachFt + POSITION_EPS_FT) {
    return refuse('out-of-reach', effectiveActorSizeRank);
  }

  if (!finite(input.route.distanceFt) || input.route.distanceFt <= POSITION_EPS_FT) return refuse('no-movement', effectiveActorSizeRank);
  if (!input.route.directionValid) return refuse('unsupported-direction', effectiveActorSizeRank);
  if (!input.route.sameSupportPlane || !input.route.supported) return refuse('unsupported-route', effectiveActorSizeRank);
  if (!input.route.pathClear) return refuse('blocked-path', effectiveActorSizeRank);
  if (!input.route.supportUnchanged) return refuse('support-changed', effectiveActorSizeRank);

  if (!input.encounter.inCombat) {
    return { allowed: true, reason: null, effectiveActorSizeRank, economy: null };
  }
  if (!Number.isSafeInteger(input.actor.aps) || Number(input.actor.aps) < OBJECT_MANIPULATION_COMBAT_INITIATION_AP) {
    return refuse('insufficient-ap', effectiveActorSizeRank);
  }
  const actorAps = Number(input.actor.aps);
  if (!finite(input.actor.movementPct) || input.actor.movementPct < 0
    || !finite(input.actor.walkingSpeedFt) || input.actor.walkingSpeedFt <= 0) {
    return refuse('insufficient-movement', effectiveActorSizeRank);
  }
  const apsForMovement = actorAps - OBJECT_MANIPULATION_COMBAT_INITIATION_AP;
  const price = priceBankedWalk(
    [[0, 0], [input.route.distanceFt, 0]],
    input.actor.movementPct,
    input.actor.walkingSpeedFt,
    apsForMovement,
  );
  if (price === null || !price.complete) return refuse('insufficient-movement', effectiveActorSizeRank);
  return {
    allowed: true,
    reason: null,
    effectiveActorSizeRank,
    economy: {
      initiationApCost: OBJECT_MANIPULATION_COMBAT_INITIATION_AP,
      movementApsSpent: price.apsSpent,
      movementPctAfter: storeMovementPct(price.remainingPct),
      apsAfter: actorAps - OBJECT_MANIPULATION_COMBAT_INITIATION_AP - price.apsSpent,
    },
  };
}

export interface ObjectManipulationCommand {
  schema: typeof OBJECT_MANIPULATION_COMMAND_SCHEMA;
  operation_id: string;
  encounter_id: string;
  actor_id: string;
  inventory_id: string;
  verb: ObjectManipulationVerb;
  expected_actor_rev: number;
  actor_origin: ObjectManipulationPoint;
  object_origin: ObjectManipulationPoint;
  object_fingerprint: string;
  object_destination: ObjectManipulationPoint;
  route: ObjectManipulationPoint[];
  /** Telekinetic push/pull (2026-10-07): decided by the browser plan (object_interaction_range_ft reach); the actor does not move. */
  telekinetic?: boolean;
  /** throw_object_max_size_type's size rank (0–7) when the browser plan widened the telekinetic size window; absent = none. */
  telekinetic_max_size_rank?: number;
}

export interface ObjectManipulationReceipt {
  schema: typeof OBJECT_MANIPULATION_RECEIPT_SCHEMA;
  operation_id: string;
  outcome: 'committed' | 'replayed' | 'refused';
  verb: ObjectManipulationVerb;
  /** Telekinetic push/pull: only the object moved (actor.before === actor.after). */
  telekinetic?: boolean;
  encounter_id: string;
  reason: ObjectManipulationReason | null;
  actor: { id: string; before: ObjectManipulationPoint; after: ObjectManipulationPoint; movement_rev: number; movement_pct: number; aps: number };
  object: { inventory_id: string; before: ObjectManipulationPoint; after: ObjectManipulationPoint };
  accepted_distance_ft: number;
  accepted_route: ObjectManipulationPoint[];
  duration_ms: number;
  compile_profile?: { cache_misses: number; hard_compiles: number; counters: Record<string, { n: number; ms?: number; b?: number }> };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Shape-only validation of the browser's command; PHP retains actor permission and persistence. */
export function isObjectManipulationCommand(value: unknown): value is ObjectManipulationCommand {
  const command = value as Partial<ObjectManipulationCommand> | null;
  return command?.schema === OBJECT_MANIPULATION_COMMAND_SCHEMA
    && command.verb !== undefined && ['push', 'pull'].includes(command.verb)
    && [command.operation_id, command.encounter_id, command.actor_id, command.inventory_id].every(id => typeof id === 'string' && UUID.test(id))
    && Number.isSafeInteger(command.expected_actor_rev) && Number(command.expected_actor_rev) >= 0
    && point(command.actor_origin ?? null) && point(command.object_origin ?? null) && point(command.object_destination ?? null)
    && typeof command.object_fingerprint === 'string' && command.object_fingerprint.length > 0 && command.object_fingerprint.length <= 256
    && Array.isArray(command.route) && command.route.length >= 2 && command.route.length <= 512
    && command.route.every(candidate => point(candidate))
    && (command.telekinetic === undefined || typeof command.telekinetic === 'boolean')
    && (command.telekinetic_max_size_rank === undefined || rank(command.telekinetic_max_size_rank));
}
